import { useCallback } from 'react';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import { useExportIoTransactionsPostMutation } from 'redux/apiSlice/io.slice';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { TColumnsState } from 'components/Base/Table/Table.types';
import { TIoSourceType } from 'data/types/io.types';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  DATE: 'date',
  GIVER: 'giver',
  RECEIVER: 'receiver',
  type: 'type',
  category: 'category',
  amount: 'amount',
  // The backend exports Families Served only when requested (GROW-4418)
  FAMILIES_SERVED: 'familiesServed',
  notes: 'notes',
  document: 'document',
  isInkindDonation: 'inKind',
};

const ALWAYS_INCLUDE_COLUMNS = ['inKindDollarValue'];

// Only these sources can log an event they attended but did not host
// (GROW-4257 FR-5, mirrors EditInputOutputModal); Event/Workshop exports
// leave the external-event columns out (GROW-4420)
const EXTERNAL_EVENT_SOURCE_TYPES: TIoSourceType[] = [
  'ELC',
  'ORGANIZATION',
  'PEOPLE',
];

const EXTERNAL_EVENT_EXPORT_COLUMNS = [
  'externalEvent',
  'eventHost',
  'eventName',
  'eventZip',
];

const SORT_FIELD_MAP: Record<string, string> = {
  DATE: 'DATE',
  GIVER: 'GIVER',
  RECEIVER: 'RECEIVER',
  FAMILIES_SERVED: 'FAMILIES_SERVED',
};

type TProps = {
  selectedRowsKeys: React.Key[];
  sourceId: string;
  sourceType: TIoSourceType;
  storageKey: string;
  selectedFilters?: Record<string, unknown>;
  sortColumn: { sortField?: string; sortDirection?: string };
};

const OutputActions = ({
  selectedRowsKeys,
  sourceId,
  sourceType,
  storageKey,
  selectedFilters,
  sortColumn,
}: TProps) => {
  const [exportIoTransactions, { isLoading: isExporting }] =
    useExportIoTransactionsPostMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[storageKey] as TColumnsState[];
    const baseColumns = !columnsState
      ? Object.values(COLUMN_KEY_TO_EXPORT_NAME)
      : columnsState
          .filter(col => col.show)
          .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
          .filter(Boolean);
    const externalEventColumns = EXTERNAL_EVENT_SOURCE_TYPES.includes(
      sourceType,
    )
      ? EXTERNAL_EVENT_EXPORT_COLUMNS
      : [];
    return [...baseColumns, ...ALWAYS_INCLUDE_COLUMNS, ...externalEventColumns];
  }, [storageKey, sourceType]);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};

      if (ids?.length) {
        filters.ids = ids;
      }

      if (sourceType === 'WORKSHOP' || sourceType === 'EVENT') {
        if (sourceId) filters.sourceId = sourceId;
      } else if (sourceId) {
        filters.participantIds = [sourceId];
      }

      if (selectedFilters?.GIVER) {
        const giverIds = Array.isArray(selectedFilters.GIVER)
          ? selectedFilters.GIVER.map(String)
          : [String(selectedFilters.GIVER)];
        filters.giverIds = giverIds;
      }
      if (selectedFilters?.RECEIVER) {
        const receiverIds = Array.isArray(selectedFilters.RECEIVER)
          ? selectedFilters.RECEIVER.map(String)
          : [String(selectedFilters.RECEIVER)];
        filters.receiverIds = receiverIds;
      }
      if (selectedFilters?.type) {
        const types = Array.isArray(selectedFilters.type)
          ? selectedFilters.type.map(String)
          : [String(selectedFilters.type)];
        filters.types = types;
      }
      if (selectedFilters?.category) {
        const categoryIds = Array.isArray(selectedFilters.category)
          ? selectedFilters.category.map(String)
          : [String(selectedFilters.category)];
        filters.categoryIds = categoryIds;
      }
      if (selectedFilters?.isInkindDonation) {
        const inKindRaw = Array.isArray(selectedFilters.isInkindDonation)
          ? String(selectedFilters.isInkindDonation[0])
          : String(selectedFilters.isInkindDonation);
        if (inKindRaw === 'true') {
          filters.inKind = true;
        }
      }

      return Object.keys(filters).length > 0 ? filters : undefined;
    },
    [sourceId, sourceType, selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportIoTransactions({
        columns: exportColumns,
        ...(buildExportFilters() && { filters: buildExportFilters() }),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportIoTransactions,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportIoTransactions({
        columns: exportColumns,
        ...(buildExportFilters(ids) && { filters: buildExportFilters(ids) }),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportIoTransactions,
    selectedRowsKeys,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  return (
    <ExportDropdown
      onExportAll={handleExportAll}
      onExportSelected={handleExportSelected}
      isExporting={isExporting}
    />
  );
};

export default OutputActions;
